/**
 * Min/max multi-resolution peak pyramid (PLAN.md §5.2) for fast waveform drawing.
 * Levels hold one (min, max) pair per 64 / 512 / 4096 samples. Built either from a
 * whole signal or incrementally (recorder), then queried per pixel column.
 */

export const PEAK_FACTORS = [64, 512, 4096] as const;

class Level {
  min: Float32Array;
  max: Float32Array;
  length = 0;
  constructor(
    readonly factor: number,
    capacity: number,
  ) {
    this.min = new Float32Array(Math.max(16, capacity));
    this.max = new Float32Array(Math.max(16, capacity));
  }
  push(mn: number, mx: number): void {
    if (this.length === this.min.length) {
      const cap = this.min.length * 2;
      const a = new Float32Array(cap);
      a.set(this.min);
      this.min = a;
      const b = new Float32Array(cap);
      b.set(this.max);
      this.max = b;
    }
    this.min[this.length] = mn;
    this.max[this.length] = mx;
    this.length++;
  }
}

export class PeakPyramid {
  readonly levels: Level[];
  totalSamples = 0;
  private finalized = false;
  // running accumulators per level
  private readonly accMin: number[];
  private readonly accMax: number[];
  private readonly accCount: number[];

  constructor(expectedSamples = 0) {
    this.levels = PEAK_FACTORS.map((f) => new Level(f, Math.ceil(expectedSamples / f) + 1));
    this.accMin = PEAK_FACTORS.map(() => Infinity);
    this.accMax = PEAK_FACTORS.map(() => -Infinity);
    this.accCount = PEAK_FACTORS.map(() => 0);
  }

  static fromSamples(x: ArrayLike<number>): PeakPyramid {
    const p = new PeakPyramid(x.length);
    p.append(x);
    p.finalize();
    return p;
  }

  /** Feed a (min,max) pair into level li's accumulator; `per` = items per output pair. */
  private feed(li: number, mn: number, mx: number): void {
    if (mn < this.accMin[li]!) this.accMin[li] = mn;
    if (mx > this.accMax[li]!) this.accMax[li] = mx;
    const per = li === 0 ? PEAK_FACTORS[0] : PEAK_FACTORS[li]! / PEAK_FACTORS[li - 1]!;
    if (++this.accCount[li]! === per) this.flush(li);
  }

  private flush(li: number): void {
    if (this.accCount[li] === 0) return;
    const mn = this.accMin[li]!;
    const mx = this.accMax[li]!;
    this.levels[li]!.push(mn, mx);
    this.accMin[li] = Infinity;
    this.accMax[li] = -Infinity;
    this.accCount[li] = 0;
    if (li + 1 < this.levels.length) this.feed(li + 1, mn, mx);
  }

  /** Append samples (incremental build). */
  append(x: ArrayLike<number>, start = 0, end = x.length): void {
    if (this.finalized) throw new Error('PeakPyramid already finalized');
    const F = PEAK_FACTORS[0];
    let mn = this.accMin[0]!;
    let mx = this.accMax[0]!;
    let c = this.accCount[0]!;
    for (let i = start; i < end; i++) {
      const v = x[i]!;
      if (v < mn) mn = v;
      if (v > mx) mx = v;
      if (++c === F) {
        this.accMin[0] = mn;
        this.accMax[0] = mx;
        this.accCount[0] = c;
        this.flush(0);
        mn = Infinity;
        mx = -Infinity;
        c = 0;
      }
    }
    this.accMin[0] = mn;
    this.accMax[0] = mx;
    this.accCount[0] = c;
    this.totalSamples += Math.max(0, end - start);
  }

  /** Flush partial bins. No more appends afterwards. */
  finalize(): void {
    if (this.finalized) return;
    for (let li = 0; li < this.levels.length; li++) this.flush(li);
    this.finalized = true;
  }

  /**
   * Fill outMin/outMax (length = pixels) for columns starting at sample `start`
   * with `spp` samples per pixel. Uses the coarsest level with factor ≤ spp, or the
   * raw samples when spp < 64 and `samples` is given. Columns with no data get NaN.
   * Returns the level index used (-1 = raw samples).
   */
  query(
    samples: ArrayLike<number> | null,
    start: number,
    spp: number,
    pixels: number,
    outMin: Float32Array,
    outMax: Float32Array,
  ): number {
    let li = -1;
    for (let i = 0; i < this.levels.length; i++) if (PEAK_FACTORS[i]! <= spp) li = i;
    if (li === -1 && !samples) li = 0;
    const total = this.totalSamples;
    for (let p = 0; p < pixels; p++) {
      const a = Math.max(0, Math.floor(start + p * spp));
      const b = Math.min(total, Math.floor(start + (p + 1) * spp));
      let mn = Infinity;
      let mx = -Infinity;
      if (b > a) {
        if (li === -1) {
          for (let i = a; i < b; i++) {
            const v = samples![i]!;
            if (v < mn) mn = v;
            if (v > mx) mx = v;
          }
        } else {
          const L = this.levels[li]!;
          const F = L.factor;
          const i0 = Math.floor(a / F);
          const i1 = Math.min(L.length, Math.ceil(b / F));
          for (let i = i0; i < i1; i++) {
            if (L.min[i]! < mn) mn = L.min[i]!;
            if (L.max[i]! > mx) mx = L.max[i]!;
          }
        }
      }
      outMin[p] = b > a ? mn : NaN;
      outMax[p] = b > a ? mx : NaN;
    }
    return li;
  }

  /** Serialise (for storing next to a recording). */
  toBuffer(): ArrayBuffer {
    this.finalize();
    const headerWords = 4 + 2 * this.levels.length;
    const dataFloats = this.levels.reduce((s, l) => s + 2 * l.length, 0);
    const buf = new ArrayBuffer(headerWords * 4 + dataFloats * 4);
    const h = new Uint32Array(buf, 0, headerWords);
    h[0] = 0x50594b50; // 'PKYP'
    h[1] = 1;
    h[2] = this.totalSamples % 2 ** 32;
    h[3] = Math.floor(this.totalSamples / 2 ** 32);
    let off = headerWords * 4;
    this.levels.forEach((l, i) => {
      h[4 + 2 * i] = l.factor;
      h[5 + 2 * i] = l.length;
      new Float32Array(buf, off, l.length).set(l.min.subarray(0, l.length));
      off += l.length * 4;
      new Float32Array(buf, off, l.length).set(l.max.subarray(0, l.length));
      off += l.length * 4;
    });
    return buf;
  }

  static fromBuffer(buf: ArrayBuffer): PeakPyramid {
    const h0 = new Uint32Array(buf, 0, 4);
    if (h0[0] !== 0x50594b50 || h0[1] !== 1) throw new Error('not a peak pyramid');
    const p = new PeakPyramid(0);
    const headerWords = 4 + 2 * p.levels.length;
    const h = new Uint32Array(buf, 0, headerWords);
    p.totalSamples = h[2]! + h[3]! * 2 ** 32;
    let off = headerWords * 4;
    p.levels.forEach((l, i) => {
      if (h[4 + 2 * i] !== l.factor) throw new Error('peak pyramid level mismatch');
      const n = h[5 + 2 * i]!;
      l.min = new Float32Array(buf.slice(off, off + n * 4));
      off += n * 4;
      l.max = new Float32Array(buf.slice(off, off + n * 4));
      off += n * 4;
      l.length = n;
    });
    p.finalized = true;
    return p;
  }
}
